export type LeafRule =
    | { operator: "binaryvalue"; question: string }
    | { operator: "naryvalue"; question: string; contains: string }
    | { operator: "naryany"; question: string };

export type RuleAst =
    | LeafRule
    | { operator: "and"; operand0: RuleAst; operand1: RuleAst }
    | { operator: "or"; operand0: RuleAst; operand1: RuleAst };

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseLeaf(value: UnknownRecord): LeafRule | null {
    const operator = value.operator;
    const question = value.question;
    if (typeof question !== "string" || question.length === 0) {
        return null;
    }
    if (operator === "binaryvalue") {
        return { operator: "binaryvalue", question };
    }
    if (operator === "naryany") {
        return { operator: "naryany", question };
    }
    if (operator === "naryvalue") {
        const contains = value.contains;
        if (typeof contains !== "string" || contains.length === 0) {
            return null;
        }
        return { operator: "naryvalue", question, contains };
    }
    return null;
}

function parseAst(value: unknown): RuleAst | null {
    if (!isRecord(value)) {
        return null;
    }
    const operator = value.operator;
    if (operator === "and" || operator === "or") {
        const left = parseAst(value.operand0);
        const right = parseAst(value.operand1);
        if (!left || !right) {
            return null;
        }
        return { operator, operand0: left, operand1: right };
    }
    return parseLeaf(value);
}

export function parseRulesString(rulesString: string): RuleAst | null {
    let json: unknown;
    try {
        json = JSON.parse(rulesString);
    } catch {
        return null;
    }
    return parseAst(json);
}

export function serializeRule(rule: RuleAst): string {
    return JSON.stringify(rule);
}

export function collectQuestionKeys(rule: RuleAst): Set<string> {
    const result = new Set<string>();
    walkKeys(rule, result);
    return result;
}

function walkKeys(rule: RuleAst, target: Set<string>): void {
    if (rule.operator === "and" || rule.operator === "or") {
        walkKeys(rule.operand0, target);
        walkKeys(rule.operand1, target);
        return;
    }
    target.add(rule.question);
}

function isLeaf(rule: RuleAst): rule is LeafRule {
    return (
        rule.operator === "binaryvalue" ||
        rule.operator === "naryvalue" ||
        rule.operator === "naryany"
    );
}

export type FlatTwoConditionRule = {
    operator: "and" | "or";
    operand0: LeafRule;
    operand1: LeafRule;
};

export function isFlatTwoConditionRule(rule: RuleAst): rule is FlatTwoConditionRule {
    if (rule.operator !== "and" && rule.operator !== "or") {
        return false;
    }
    return isLeaf(rule.operand0) && isLeaf(rule.operand1);
}

export interface FlatTwoConditionParts {
    join: "and" | "or";
    first: LeafRule;
    second: LeafRule;
}

export function decomposeFlatTwoConditionRule(rule: RuleAst): FlatTwoConditionParts {
    if (!isFlatTwoConditionRule(rule)) {
        throw new Error("decomposeFlatTwoConditionRule called on non-flat rule");
    }
    return {
        join: rule.operator,
        first: rule.operand0,
        second: rule.operand1,
    };
}

export function composeFlatTwoConditionRule(parts: FlatTwoConditionParts): RuleAst {
    return {
        operator: parts.join,
        operand0: parts.first,
        operand1: parts.second,
    };
}

export interface CandidateDependsOnEditingArgs {
    candidateRule: RuleAst | null;
    editingKey: string;
    allQuestions: ReadonlyArray<{ key: string; rulesString: string | null }>;
}

export function candidateDependsOnEditing(args: CandidateDependsOnEditingArgs): boolean {
    if (!args.candidateRule) {
        return false;
    }
    const visited = new Set<string>();
    const queue: RuleAst[] = [args.candidateRule];
    const ruleByKey = new Map<string, RuleAst | null>();
    for (const q of args.allQuestions) {
        ruleByKey.set(q.key, q.rulesString ? parseRulesString(q.rulesString) : null);
    }
    while (queue.length > 0) {
        const rule = queue.shift()!;
        const keys = Array.from(collectQuestionKeys(rule));
        for (const key of keys) {
            if (key === args.editingKey) {
                return true;
            }
            if (visited.has(key)) {
                continue;
            }
            visited.add(key);
            const next = ruleByKey.get(key);
            if (next) {
                queue.push(next);
            }
        }
    }
    return false;
}
